import React, { useState, useEffect, useContext, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, IconButton, TextField, Dialog,
  DialogTitle, DialogActions, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import config from '../../config';
import { formatEuro } from '../../functions/formatNumber';
import { TipusMovIcon } from './IconMap';
import { strDateToShort, strDateToDDMMYYYY } from '../../functions/formatDate';
import { getColorForAmount } from '../../functions/colors';
import AppContext from '../AppContext';
import { SelectConcepteTextField } from './SelectConcepte';
import ConcepteMovementDisplay from './ConcepteMovementDisplay';

const MovimentItemEdit = ({ movementData, showYear, onError, listConceptes, onEditComplete, pathInfo, enrichedTree: propEnrichedTree }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editData, setEditData] = useState({ ...movementData });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const value = movementData;
  const { userapp: paramUserapp } = useParams();
  const userapp = paramUserapp;

  const { ctxUser, conceptesList } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';
  const targetConceptList = listConceptes || conceptesList || [];

  const navigate = useNavigate();

  const handleDetailMov = (movement) => {
    if(!isEditing && !isDeleting) {
      navigate(`/${userapp}/cmovdetail/${movement.id}`);
    }
  };    

  const conceptObj = conceptesList?.find(c => c.nom === editData.concepte);
  const isInvestment = conceptObj?.tipus === 'INVERSIO';
  const colorImport = getColorForAmount(value.importe, isInvestment);
  let title = null;
  if(value.moviment?.mesInfo && value.moviment?.mesInfoDetall) {
    title = `Info: ${value.moviment.mesInfo}\n+Info: ${value.moviment.mesInfoDetall}`
  }
  else if (value.moviment?.mesInfo) {
    title = `Info: ${value.moviment.mesInfo}`
  }
  else if (value.moviment?.mesInfoDetall) {
    title = `Info: ${value.moviment.mesInfoDetall}`
  }

  const handleEditToggle = (e) => {
    e.stopPropagation();
    setIsEditing(!isEditing);
  };

  const handleDeleteConfirm = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleting(!isDeleting);
    setShowDeleteConfirm(true);
  };

  const handleCancelEdit = (e) => {
      setIsEditing(false);
      setEditData({ ...movementData });
      e.stopPropagation();
  };

  const handleCancelDelete = (e) => {
    setShowDeleteConfirm(false);
    setIsDeleting(false);
    e.stopPropagation();
  };

  
  const onChangeConcepte = (e, selectedNode) => {
    if (e.stopPropagation) e.stopPropagation();
    const node = selectedNode || e.target?.node;
    if (node) {
      const isN3 = Boolean(node.nodeN3);
      setEditData(prev => ({
        ...prev,
        concepte: null, // El concepte clàssic el resol oficialment el backend
        nodeFulla: node,
        id_node_fulla: node.id,
        idNodeFulla: node.id,
        conceptePath: node.conceptePath,
        nodeN1: node.nodeN1 || null,
        nodeN2: node.nodeN2 || null,
        nodeN3: isN3 ? (node.nodeN3 || null) : null,
        id_node_n1: node.nodeN1?.id || null,
        id_node_n2: node.nodeN2?.id || null,
        id_node_n3: isN3 ? (node.nodeN3?.id || null) : null,
        idNodeN1: node.nodeN1?.id || null,
        idNodeN2: node.nodeN2?.id || null,
        idNodeN3: isN3 ? (node.nodeN3?.id || null) : null,
      }));
    } else {
      setEditData(prev => ({
        ...prev,
        concepte: e.target?.value || '',
        nodeFulla: null,
        id_node_fulla: null,
        idNodeFulla: null,
        conceptePath: null,
        nodeN1: null,
        nodeN2: null,
        nodeN3: null,
        id_node_n1: null,
        id_node_n2: null,
        id_node_n3: null,
        idNodeN1: null,
        idNodeN2: null,
        idNodeN3: null,
      }));
    }
  };

  const handleSave = () => {
    const toNodeRef = (val) => {
      if (!val) return null;
      const id = typeof val === 'object' ? val.id : val;
      return id ? { id: Number(id) } : null;
    };

    const isHierarchyNode = Boolean(
      editData.nodeFulla || editData.idNodeFulla || editData.id_node_fulla || editData.nodeN1 || editData.idNodeN1
    );

    const payload = {
      ...editData,
      concepte: isHierarchyNode ? null : (editData.concepte || null),
      nodeN1: toNodeRef(editData.nodeN1 || editData.idNodeN1 || editData.id_node_n1),
      nodeN2: toNodeRef(editData.nodeN2 || editData.idNodeN2 || editData.id_node_n2),
      nodeN3: toNodeRef(editData.nodeN3 || editData.idNodeN3 || editData.id_node_n3),
      nodeFulla: toNodeRef(editData.nodeFulla || editData.idNodeFulla || editData.id_node_fulla),
    };

    axios.put(`${config.apiBaseUrl}/v1/concmovs/${movementData.id}`,
      payload,
      { headers: { Authorization: `${jwtToken}` } }
    ).then((response) => {
      setIsEditing(false);
      if (response.data) {
        setEditData({ ...response.data });
      }
      if (onEditComplete) onEditComplete(response.data);
    }).catch((err) => {
      if (onError) onError(err, 'guardar');
      else console.error('Error al guardar:', err);
    });
  };



  

  const doDelete = () => {
    axios.delete(`${config.apiBaseUrl}/v1/concmovs/${movementData.id}`,
    { headers: { Authorization: `${jwtToken}` } }
    ).then(() => {
      setShowDeleteConfirm(false);
      if (onEditComplete) onEditComplete();
    }).catch((err) => {
      if (onError) onError(err, 'borrar');
      else console.error('Error al borrar:', err);
    });
  };

  useEffect(() => {
    //console.log('Detall Moviment useEffect:', data);
    setEditData({...movementData});
  }, [movementData]);

  return (
    <Grid container spacing={2} alignItems="center" 
      onClick={() => handleDetailMov(value)}
      title={title}
      sx={{ 
        marginBottom: 1,
        paddingLeft: '10px',
        paddingRight: '10px',
        transition: 'background-color 0.3s',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)', // Cambia el color de fondo al pasar el ratón
        },
      }}>
      <Grid size={{xs: 14, sm: 1 }}>
        { showYear &&
            <Typography variant="small" sx={{ fontSize: '0.80rem' }}>
              { strDateToDDMMYYYY(value.data) }
            </Typography>
        }

        { !showYear &&
            <Typography variant="small">
              { strDateToShort(value.data) }
            </Typography>
        }
      </Grid>

      <Grid size={{xs: 12, sm: 0.5 }} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <TipusMovIcon tipus={value.moviment.tipus}/>
      </Grid>

      <Grid size={{xs: 12, sm: 2 }} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isEditing ? (
        <SelectConcepteTextField
          value={
            editData.conceptePath ||
            value.conceptePath ||
            (editData.id_node_fulla ? `node:${editData.id_node_fulla}` : null) ||
            (value.id_node_fulla ? `node:${value.id_node_fulla}` : null) ||
            (value.idNodeFulla ? `node:${value.idNodeFulla}` : null) ||
            (typeof value.nodeFulla === 'number' ? `node:${value.nodeFulla}` : null) ||
            (editData.nodeFulla?.id ? `node:${editData.nodeFulla.id}` : null) ||
            (typeof editData.nodeFulla === 'number' ? `node:${editData.nodeFulla}` : null) ||
            editData.concepte ||
            value.concepte
          }
          onChange={onChangeConcepte}
          listConceptes={targetConceptList}
        />
      ) : (
        <ConcepteMovementDisplay
          movement={editData}
          pathInfo={pathInfo}
          enrichedTree={propEnrichedTree}
          maxWidth="180px"
        />
      )}
      </Grid>

      <Grid size={{xs: 12, sm: 4 }}>
        <Typography variant="small">
          {value.moviment.moviment}
        </Typography>
      </Grid>

      <Grid size={{xs: 12, sm: 2 }}>
        {isEditing ? (
              <TextField
              fullWidth
              //label="Descripció"
              value={editData.descripcio}
              onChange={(e) => setEditData({ ...editData, descripcio: e.target.value })}
              />
          ) : (
              <Typography variant="small" sx={{ fontSize: '0.80rem' }}>
                {editData.descripcio ? editData.descripcio : 'N/D'}
              </Typography>
          )}          
      </Grid>

      {/* Columna derecha: Saldo del mes y saldo acumulado */}
      <Grid size={{xs: 12, sm: 1.5 }} container direction="column" alignItems="flex-end" 
        sx={{ gap: '1px' }}>
        <Typography variant="body1" fontWeight="bold" color={colorImport} >
          {formatEuro(value.importe)}
        </Typography>
      </Grid>

      <Grid size={{xs: 12, sm: 1 }} display="flex" justifyContent="flex-end"
        sx={{ gap: '1px' }}>
          {isEditing ? (
            <>
              <IconButton onClick={handleSave}><SaveIcon /></IconButton>
              <IconButton onClick={handleCancelEdit}><CancelIcon /></IconButton>
            </>
          ) : (
            <>
              <IconButton onClick={handleEditToggle}><EditIcon /></IconButton>
              <IconButton onClick={handleDeleteConfirm}><DeleteIcon /></IconButton>
            </>
          )}          
      </Grid>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
          <DialogTitle>Confirmar Eliminació</DialogTitle>
          <DialogActions>
            <Button onClick={handleCancelDelete}>Cancel·lar</Button>
            <Button onClick={() => { doDelete() }}>Eliminar</Button>
          </DialogActions>
      </Dialog>

    </Grid>
  );
};

export default memo(MovimentItemEdit);